'use client'

import { useState } from 'react'
import { ReceiptText } from 'lucide-react'
import OrderCard from './OrderCard'
import Pagination from '@/components/elements/Pagination'
import Skeleton from '@/components/elements/Skeleton'
import EmptyState from '@/components/elements/EmptyState'
import { getVendorPrimaryColor, resolveColorHex } from '@/utils/util'

const TABS = [
  { id: 'all', label: 'Бүгд', filter: () => true },
  { id: 'active', label: 'Идэвхтэй', filter: (o: any) => ['pending', 'paid', 'ready', 'shipped'].includes(o?.status) },
  { id: 'delivered', label: 'Хүргэгдсэн', filter: (o: any) => o?.status === 'delivered' },
  { id: 'refunded', label: 'Буцаалт', filter: (o: any) => ['refunded', 'partiallyRefunded', 'canceled'].includes(o?.status) },
]

export default function OrderList({ orderList, vendor }: { orderList: any, vendor?: any }) {
  const primaryHex = resolveColorHex(getVendorPrimaryColor(vendor))
  const [prevOrderList, setPrevOrderList] = useState<any>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [tab, setTab] = useState('all')

  if (orderList !== prevOrderList) {
    setPrevOrderList(orderList)
    if (orderList !== undefined) setIsLoading(false)
  }

  if (isLoading) return <OrderListSkeleton />

  const orders: any[] = orderList ? orderList : []
  const counts = TABS.reduce<Record<string, number>>((acc, t) => {
    acc[t.id] = orders.filter(t.filter).length
    return acc
  }, {})
  const activeTab = TABS.find(t => t.id === tab) || TABS[0]
  const visible = orders.filter(activeTab.filter)

  return (
    <div className="sm:p-3 space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.7%]">Захиалга</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Нийт <strong className="text-foreground tabular-nums">{orders.length}</strong> захиалга
          {counts.active > 0 && <> · <strong className="text-foreground tabular-nums">{counts.active}</strong> идэвхтэй</>}
        </p>
      </div>

      {orders.length > 0 && (
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="px-4 flex items-center gap-1 flex-wrap">
            {TABS.map(t => {
              const isActive = tab === t.id
              const count = counts[t.id]
              return (
                <button key={t.id} onClick={() => setTab(t.id)} className={`relative h-12 px-3.5 text-sm whitespace-nowrap transition-colors inline-flex items-center gap-2 ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                  {t.label}
                  <span className={`text-[11px] tabular-nums px-1.5 h-5 inline-flex items-center justify-center rounded-md ${isActive ? 'bg-foreground text-white' : 'bg-zinc-100 text-muted-foreground'}`}>{count}</span>
                  {isActive && <span className="absolute -bottom-px left-0 right-0 h-0.5" style={{ background: primaryHex }} />}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {orders.length > 0 ? (
        <Pagination items={visible} component={({ item }: { item: any }) => <OrderCard order={item} vendor={vendor} />} limit={10} empty={OrderNotFound} showSearch={false} searchFields={[]} containerClassName="flex flex-col !gap-y-3 !grid-cols-none !grid-rows-none" />
      ) : (
        <OrderNotFound />
      )}
    </div>
  )
}

export function OrderNotFound() {
  return <EmptyState icon={ReceiptText} title="Захиалга байхгүй байна" description="Таны хийсэн захиалгууд энд харагдах болно." />
}

function OrderListSkeleton() {
  return (
    <div className="sm:p-3 space-y-5">
      <div className="flex flex-col gap-1">
        <Skeleton className="w-40 h-8" />
        <Skeleton className="w-64 h-5" />
      </div>
      <Skeleton className="w-full h-24 rounded-xl" />
      <div className="flex flex-col gap-3">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="w-full h-40 rounded-xl" />)}
      </div>
    </div>
  )
}
